import React, { useState, useEffect, useRef } from "react";
import Header from "../../common/layout/header";
import Footer from "../../common/layout/footer";
import Sidebar from "../../common/sidebar";
import DataTable from "react-data-table-component";
import Sortable from "sortablejs";
import { http } from "../../helper";
import { BaseUrl } from "../../common/config";
import { useTranslation } from "react-i18next";
import { canAction } from "../../common/permissionhelper";

const FAQ = () => {
  const { t } = useTranslation();
  const [faqList, setFaqList] = useState([]);
  const [tempList, setTempList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const tableBodyRef = useRef(null); // For Drag & Drop
  // Login and Permission Check
  if (!sessionStorage.getItem("isLoggedIn")) window.location.href = "/";
  const authData = JSON.parse(sessionStorage.getItem("authData"));
  if (
    authData &&
    !authData.adminRole.permissions.includes("manage_foodservicepartners")
  ) {
    window.location.href = "/unauthorized";
  }

  useEffect(() => {
    fetchFAQList();
  }, []);
  // Fetch All FAQs
  const fetchFAQList = async () => {
    setIsLoading(true);
    try {
      const URL = BaseUrl + `admin/faqlist`;
      const response = await http.get(URL);
      if (response.data.success) {
        const sorted = response.data.data.sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
        setFaqList(sorted);
        setTempList(sorted);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };
  // Handle Add FAQ
  const addFAQ = () => {
    window.location.href = `/vendorhub/faq/add`;
  };
  // Handle Edit
  const editFAQ = (faq) => {
    window.location.href = `/vendorhub/faq/edit/${faq._id}`;
  };
  // Handle Delete
  const confirmDeleteFAQ = (faq) => {
    setSelectedFAQ(faq);
    setVisibleAlert(true);
  };

  const deleteFAQ = async () => {
    try {
      const URL = BaseUrl + `admin/deletefaq/${selectedFAQ._id}`;
      const resp = await http.delete(URL);
      if (resp.data.success) window.location.reload();
    } catch (err) {
      console.log(err);
    }
  };

  const closeAlert = () => {
    setVisibleAlert(false);
    setSelectedFAQ(null);
  };
  // Search FAQ
  const handleSearch = (value) => {
    setSearchText(value);
    filterFAQ(value);
  };

  const filterFAQ = (value) => {
    if (!value.trim()) {
      setFaqList(tempList);
      return;
    }
    const search = value.toLowerCase();
    const filtered = tempList.filter((item) =>
      item.question.toLowerCase().includes(search),
    );
    setFaqList(filtered);
  };

  const resetSearch = () => {
    setSearchText("");
    setFaqList(tempList);
  };

  const canReorder = canAction("manage_foodservicepartners", "reorder");
  // Drag & Drop Sorting Logic
  useEffect(() => {
    if (!canReorder) return;
    if (!tableBodyRef.current) return;
    const sortable = Sortable.create(tableBodyRef.current, {
      animation: 150,
      handle: ".drag-handle",
      onEnd: async ({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex) return;
        // Copy current list
        const updatedList = [...faqList];
        // Remove item from old index & place at new index
        const [movedRow] = updatedList.splice(oldIndex, 1);
        updatedList.splice(newIndex, 0, movedRow);
        // Reassign sortOrder locally to avoid blank UI
        const reorderedList = updatedList.map((item, index) => ({
          ...item,
          sortOrder: index + 1,
        }));
        // Update UI immediately
        setFaqList(reorderedList);
        setTempList(reorderedList);
        // Send to backend
        try {
          const URL = BaseUrl + `admin/reorderfaq`;
          await http.put(URL, reorderedList);
        } catch (err) {
          console.log(err);
        }
      },
    });
    return () => sortable.destroy();
  }, [faqList, canReorder]);
  // DataTable Columns
  const columns = [
    {
      name: t("reorder_txt"),
      cell: () => (
        <span className="drag-handle" style={{ cursor: "grab" }}>
          ≡
        </span>
      ),
      width: "60px",
    },
    {
      name: t("question_txt"),
      selector: (row) => row.question,
      sortable: true,
    },
    {
      name: t("status_txt"),
      selector: (row) =>
        row.status === "A" ? t("active_txt") : t("inactive_txt"),
      sortable: true,
    },
    {
      name: t("action_txt"),
      cell: (row) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="button" onClick={() => editFAQ(row)}>
            <i className="fa fa-pencil"></i>
          </button>
          <button className="button" onClick={() => confirmDeleteFAQ(row)}>
            <i className="fa fa-trash"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  const customStyles = {
    rows: { style: { minHeight: "40px" } },
    headCells: { style: { fontWeight: "600", padding: "10px" } },
    cells: { style: { padding: "10px" } },
  };

  return (
    <section className="dashboard-block">
      <Sidebar />
      <div className="dash-left">
        <Header />
        <section className="cart-block">
          <div className="container">
            <h1 className="checkout-title">{t("faq_management_txt")}</h1>
            <div className="right-table-outer-wrap">
              {/* Search + Add */}
              <div className="table-filter search-orders">
                <div className="table-search-reset">
                  <div className="table-search">
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="form-control"
                      placeholder={t("search_questions_txt")}
                    />
                  </div>
                  <button className="submitbtn button search-ord-btn">
                    <i className="fa fa-search"></i>
                  </button>
                  <button className="resetbtn" onClick={resetSearch}>
                    {t("reset_txt")}
                  </button>
                </div>
                {canAction("manage_foodservicepartners", "add") && (
                  <div className="add-masterbtn">
                    <button className="addbtn" onClick={addFAQ}>
                      {t("add_txt")}
                    </button>
                  </div>
                )}
              </div>
              {isLoading && (
                <div
                  className="text-center"
                  style={{
                    fontSize: "25px",
                    padding: "20px",
                    fontWeight: "1000",
                  }}
                >
                  {t("loading_txt")}
                </div>
              )}
              {/* Drag & Drop Enabled Table */}
              {faqList.length > 0 && (
                <div className="table_overflow">
                  <div style={{ overflowX: "auto" }}>
                    <table
                      className="rdt_Table no-datatable faq-table"
                      style={{ width: "100%" }}
                    >
                      <thead>
                        <tr>
                          {columns.map((col, idx) => (
                            <th key={idx} style={{ padding: "10px" }}>
                              {col.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody ref={tableBodyRef}>
                        {faqList.map((row) => {
                          const canEdit = canAction(
                            "manage_foodservicepartners",
                            "edit",
                          );
                          return (
                            <tr key={row._id} data-id={row._id}>
                              <td>
                                <span className="drag-handle">≡</span>
                              </td>
                              <td>{row.question}</td>
                              <td>
                                {row.status === "A"
                                  ? t("active_txt")
                                  : t("inactive_txt")}
                              </td>
                              <td>
                                <a
                                  className="button title-tooltip title-up"
                                  data-title={canEdit ? t("edit_txt") : t("view_txt")}
                                  onClick={() => editFAQ(row)}
                                >
                                  <i
                                    className={`fa ${canEdit ? "fa-pencil" : "fa-eye"}`}
                                  ></i>
                                </a>
                                {canAction(
                                  "manage_foodservicepartners",
                                  "delete",
                                ) && (
                                  <a
                                    className="button title-tooltip title-up"
                                    data-title={t("delete_txt")}
                                    onClick={() => confirmDeleteFAQ(row)}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {faqList.length === 0 && !isLoading && (
                <div
                  className="no-records-found text-center"
                  style={{ width: "40%" }}
                >
                  {t("no_record_txt")}
                </div>
              )}
            </div>
            {/* Delete Popup */}
            {visibleAlert && (
              <div className="custom-alert-pop paylater-popup">
                <div className="custom-alert-container">
                  <div className="custom-alert-wrap">
                    <div className="custom-alert-header">
                      <h4>{t("alert_txt")}</h4>
                    </div>
                    <div className="custom-alert-body">
                      <p>{t("delete_confirm_txt")}</p>
                      <div className="alert-buttons">
                        <a
                          onClick={deleteFAQ}
                          className="popup-alert-btn button"
                        >
                          {t("ok_txt")}
                        </a>
                        <a
                          onClick={closeAlert}
                          className="popup-alert-btn button"
                        >
                          {t("cancel_txt")}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        <Footer />
      </div>
    </section>
  );
};

export default FAQ;
